// src/store/slices/businessVerificationSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import BusinessVerificationService from '../services/BusinessVerificationService';

// ============================================================
// ERROR HANDLING UTILITY
// ============================================================
const handleError = (error) => {
  console.log('Full error object:', JSON.stringify(error, null, 2));
  
  let errorMessage = 'An unexpected error occurred. Please try again.';
  
  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error?.data?.error?.message) {
    errorMessage = error.data.error.message;
  } else if (error?.error && typeof error.error === 'string') {
    errorMessage = error.error;
  } else if (error?.message) {
    errorMessage = error.message;
  } else if (error?.response?.data?.error?.message) {
    errorMessage = error.response.data.error.message;
  } else if (error?.response?.data?.error && typeof error.response.data.error === 'string') {
    errorMessage = error.response.data.error;
  } else if (error?.response?.data?.message) {
    errorMessage = error.response.data.message;
  } else if (error?.response?.data?.data?.error?.message) {
    errorMessage = error.response.data.data.error.message;
  } else if (error?.success === false && error?.error) {
    errorMessage = error.error;
  } else if (error?.data && error?.statusCode) {
    if (error.data.error?.message) {
      errorMessage = error.data.error.message;
    } else if (error.data.message) {
      errorMessage = error.data.message;
    }
  } else if (error?.response) {
    if (error.response.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response.statusText) {
      errorMessage = error.response.statusText;
    }
  } else if (error?.error) {
    if (typeof error.error === 'string') {
      errorMessage = error.error;
    } else if (error.error.message) {
      errorMessage = error.error.message;
    }
  }
  
  console.log('Extracted error message:', errorMessage);
  return errorMessage;
};

// Initial state
const initialState = {
  // Verification status
  verificationStatus: null,
  verified: false,
  verificationData: null,
  
  // Documents
  documents: [],
  documentTypes: [],
  
  // Requirements
  requirements: null,
  
  // History
  history: [],
  
  // Pending verifications (admin)
  pendingVerifications: {
    items: [],
    pagination: {
      total: 0,
      page: 1,
      limit: 20,
      pages: 0
    }
  },
  
  // Statistics (admin)
  statistics: null,
  
  // Statuses
  statuses: null,
  
  // UI State
  loading: false,
  submitting: false,
  error: null,
  success: false,
  message: null
};

// ============================================================
// ASYNC THUNKS
// ============================================================

/**
 * Submit provider for verification
 */
export const submitForVerification = createAsyncThunk(
  'businessVerification/submitForVerification',
  async ({ providerId, documents }, { rejectWithValue }) => {
    try {
      const response = await BusinessVerificationService.submitForVerification(
        providerId,
        documents
      );
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get verification status
 */
export const getVerificationStatus = createAsyncThunk(
  'businessVerification/getVerificationStatus',
  async (providerId, { rejectWithValue }) => {
    try {
      const response = await BusinessVerificationService.getVerificationStatus(providerId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get verification requirements
 */
export const getVerificationRequirements = createAsyncThunk(
  'businessVerification/getVerificationRequirements',
  async (providerId, { rejectWithValue }) => {
    try {
      const response = await BusinessVerificationService.getVerificationRequirements(providerId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Update verification documents
 */
export const updateVerificationDocuments = createAsyncThunk(
  'businessVerification/updateVerificationDocuments',
  async ({ providerId, documents }, { rejectWithValue }) => {
    try {
      const response = await BusinessVerificationService.updateVerificationDocuments(
        providerId,
        documents
      );
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Remove verification document
 */
export const removeVerificationDocument = createAsyncThunk(
  'businessVerification/removeVerificationDocument',
  async ({ providerId, documentId }, { rejectWithValue }) => {
    try {
      const response = await BusinessVerificationService.removeVerificationDocument(
        providerId,
        documentId
      );
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get verification history
 */
export const getVerificationHistory = createAsyncThunk(
  'businessVerification/getVerificationHistory',
  async (providerId, { rejectWithValue }) => {
    try {
      const response = await BusinessVerificationService.getVerificationHistory(providerId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);


/**
 * Upload verification document (individual file upload)
 * POST /service-provider/:providerId/verification/upload-document
 */

export const uploadVerificationDocument = createAsyncThunk(
  'businessVerification/uploadDocument',
  async ({ providerId, formData, docType, fileName }, { rejectWithValue }) => {
    try {
      const response = await BusinessVerificationService.uploadVerificationDocument(
        providerId,
        formData
      );
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get document types
 */
export const getDocumentTypes = createAsyncThunk(
  'businessVerification/getDocumentTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await BusinessVerificationService.getDocumentTypes();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get verification statuses
 */
export const getVerificationStatuses = createAsyncThunk(
  'businessVerification/getVerificationStatuses',
  async (_, { rejectWithValue }) => {
    try {
      const response = await BusinessVerificationService.getVerificationStatuses();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ============================================================
// ADMIN THUNKS
// ============================================================

/**
 * Get pending verifications (Admin only)
 */
export const getPendingVerifications = createAsyncThunk(
  'businessVerification/getPendingVerifications',
  async ({ page = 1, limit = 20 }, { rejectWithValue }) => {
    try {
      const response = await BusinessVerificationService.getPendingVerifications(page, limit);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get verification statistics (Admin only)
 */
export const getVerificationStatistics = createAsyncThunk(
  'businessVerification/getVerificationStatistics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await BusinessVerificationService.getVerificationStatistics();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Verify a provider (Admin only)
 */
export const verifyProvider = createAsyncThunk(
  'businessVerification/verifyProvider',
  async ({ providerId, adminNotes = null }, { rejectWithValue }) => {
    try {
      const response = await BusinessVerificationService.verifyProvider(
        providerId,
        adminNotes
      );
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Reject a provider (Admin only)
 */
export const rejectProvider = createAsyncThunk(
  'businessVerification/rejectProvider',
  async ({ providerId, reason }, { rejectWithValue }) => {
    try {
      const response = await BusinessVerificationService.rejectProvider(
        providerId,
        reason
      );
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Suspend a provider (Admin only)
 */
export const suspendProvider = createAsyncThunk(
  'businessVerification/suspendProvider',
  async ({ providerId, reason }, { rejectWithValue }) => {
    try {
      const response = await BusinessVerificationService.suspendProvider(
        providerId,
        reason
      );
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Reinstate a provider (Admin only)
 */
export const reinstateProvider = createAsyncThunk(
  'businessVerification/reinstateProvider',
  async ({ providerId }, { rejectWithValue }) => {
    try {
      const response = await BusinessVerificationService.reinstateProvider(
        providerId
      );
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Bulk verify providers (Admin only)
 */
export const bulkVerifyProviders = createAsyncThunk(
  'businessVerification/bulkVerifyProviders',
  async ({ providerIds }, { rejectWithValue }) => {
    try {
      const response = await BusinessVerificationService.bulkVerifyProviders(
        providerIds
      );
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Bulk reject providers (Admin only)
 */
export const bulkRejectProviders = createAsyncThunk(
  'businessVerification/bulkRejectProviders',
  async ({ providerIds, reason }, { rejectWithValue }) => {
    try {
      const response = await BusinessVerificationService.bulkRejectProviders(
        providerIds,
        reason
      );
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ============================================================
// SLICE
// ============================================================

const businessVerificationSlice = createSlice({
  name: 'businessVerification',
  initialState,
  reducers: {
    // Reset state
    resetVerificationState: (state) => {
      state.verificationStatus = null;
      state.verified = false;
      state.verificationData = null;
      state.documents = [];
      state.requirements = null;
      state.history = [];
      state.loading = false;
      state.submitting = false;
      state.error = null;
      state.success = false;
      state.message = null;
    },
    
    // Reset error
    clearVerificationError: (state) => {
      state.error = null;
    },
    
    // Reset success
    clearVerificationSuccess: (state) => {
      state.success = false;
      state.message = null;
    },
    
    // Reset documents
    resetDocuments: (state) => {
      state.documents = [];
    },
    
    // Reset pending verifications
    resetPendingVerifications: (state) => {
      state.pendingVerifications = {
        items: [],
        pagination: {
          total: 0,
          page: 1,
          limit: 20,
          pages: 0
        }
      };
    },
    
    // Reset statistics
    resetStatistics: (state) => {
      state.statistics = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // ============================================================
      // submitForVerification
      // ============================================================
      .addCase(submitForVerification.pending, (state) => {
        state.submitting = true;
        state.error = null;
        state.success = false;
      })
      .addCase(submitForVerification.fulfilled, (state, action) => {
        state.submitting = false;
        state.success = true;
        state.message = action.payload?.message || 'Verification submitted successfully';
        state.verificationData = action.payload?.data || null;
        state.verificationStatus = 'pending';
      })
      .addCase(submitForVerification.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload || 'Failed to submit verification';
        state.success = false;
      })

      // ============================================================
      // getVerificationStatus
      // ============================================================
      .addCase(getVerificationStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVerificationStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.verificationData = action.payload?.data || null;
        state.verificationStatus = action.payload?.data?.verificationStatus || null;
        state.verified = action.payload?.data?.verified || false;
        state.documents = action.payload?.data?.documents || [];
        state.requirements = action.payload?.data?.requirements || null;
        state.history = action.payload?.data?.history || [];
      })
      .addCase(getVerificationStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get verification status';
      })

      // ============================================================
      // getVerificationRequirements
      // ============================================================
      .addCase(getVerificationRequirements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVerificationRequirements.fulfilled, (state, action) => {
        state.loading = false;
        state.requirements = action.payload?.data || null;
      })
      .addCase(getVerificationRequirements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get verification requirements';
      })

      // ============================================================
      // updateVerificationDocuments
      // ============================================================
      .addCase(updateVerificationDocuments.pending, (state) => {
        state.submitting = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateVerificationDocuments.fulfilled, (state, action) => {
        state.submitting = false;
        state.success = true;
        state.message = action.payload?.message || 'Documents updated successfully';
        state.documents = action.payload?.data?.documents || [];
      })
      .addCase(updateVerificationDocuments.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload || 'Failed to update documents';
        state.success = false;
      })

      // ============================================================
      // removeVerificationDocument
      // ============================================================
      .addCase(removeVerificationDocument.pending, (state) => {
        state.submitting = true;
        state.error = null;
        state.success = false;
      })
      .addCase(removeVerificationDocument.fulfilled, (state, action) => {
        state.submitting = false;
        state.success = true;
        state.message = action.payload?.message || 'Document removed successfully';
        // Remove the document from the list
        if (action.payload?.data?.documentId) {
          state.documents = state.documents.filter(
            doc => doc.id !== action.payload.data.documentId
          );
        }
      })
      .addCase(removeVerificationDocument.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload || 'Failed to remove document';
        state.success = false;
      })

      // ============================================================
      // getVerificationHistory
      // ============================================================
      .addCase(getVerificationHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVerificationHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.history = action.payload?.data?.history || [];
      })
      .addCase(getVerificationHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get verification history';
      })

      // ============================================================
      // getDocumentTypes
      // ============================================================
      .addCase(getDocumentTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDocumentTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.documentTypes = action.payload?.data || [];
      })
      .addCase(getDocumentTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get document types';
      })

      // ============================================================
      // getVerificationStatuses
      // ============================================================
      .addCase(getVerificationStatuses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVerificationStatuses.fulfilled, (state, action) => {
        state.loading = false;
        state.statuses = action.payload?.data || null;
      })
      .addCase(getVerificationStatuses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get verification statuses';
      })

      // ============================================================
      // ADMIN: getPendingVerifications
      // ============================================================
      .addCase(getPendingVerifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPendingVerifications.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingVerifications = {
          items: action.payload?.data?.items || [],
          pagination: action.payload?.data?.pagination || {
            total: 0,
            page: 1,
            limit: 20,
            pages: 0
          }
        };
      })
      .addCase(getPendingVerifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get pending verifications';
      })

      // ============================================================
      // ADMIN: getVerificationStatistics
      // ============================================================
      .addCase(getVerificationStatistics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVerificationStatistics.fulfilled, (state, action) => {
        state.loading = false;
        state.statistics = action.payload?.data || null;
      })
      .addCase(getVerificationStatistics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get verification statistics';
      })

      // ============================================================
      // ADMIN: verifyProvider
      // ============================================================
      .addCase(verifyProvider.pending, (state) => {
        state.submitting = true;
        state.error = null;
        state.success = false;
      })
      .addCase(verifyProvider.fulfilled, (state, action) => {
        state.submitting = false;
        state.success = true;
        state.message = action.payload?.message || 'Provider verified successfully';
        state.verificationStatus = 'verified';
        state.verified = true;
      })
      .addCase(verifyProvider.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload || 'Failed to verify provider';
        state.success = false;
      })

      // ============================================================
      // ADMIN: rejectProvider
      // ============================================================
      .addCase(rejectProvider.pending, (state) => {
        state.submitting = true;
        state.error = null;
        state.success = false;
      })
      .addCase(rejectProvider.fulfilled, (state, action) => {
        state.submitting = false;
        state.success = true;
        state.message = action.payload?.message || 'Provider rejected successfully';
        state.verificationStatus = 'rejected';
        state.verified = false;
      })
      .addCase(rejectProvider.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload || 'Failed to reject provider';
        state.success = false;
      })

      // ============================================================
      // ADMIN: suspendProvider
      // ============================================================
      .addCase(suspendProvider.pending, (state) => {
        state.submitting = true;
        state.error = null;
        state.success = false;
      })
      .addCase(suspendProvider.fulfilled, (state, action) => {
        state.submitting = false;
        state.success = true;
        state.message = action.payload?.message || 'Provider suspended successfully';
        state.verificationStatus = 'suspended';
        state.verified = false;
      })
      .addCase(suspendProvider.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload || 'Failed to suspend provider';
        state.success = false;
      })

      // ============================================================
      // ADMIN: reinstateProvider
      // ============================================================
      .addCase(reinstateProvider.pending, (state) => {
        state.submitting = true;
        state.error = null;
        state.success = false;
      })
      .addCase(reinstateProvider.fulfilled, (state, action) => {
        state.submitting = false;
        state.success = true;
        state.message = action.payload?.message || 'Provider reinstated successfully';
        state.verificationStatus = 'verified';
        state.verified = true;
      })
      .addCase(reinstateProvider.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload || 'Failed to reinstate provider';
        state.success = false;
      })

      // ============================================================
      // ADMIN: bulkVerifyProviders
      // ============================================================
      .addCase(bulkVerifyProviders.pending, (state) => {
        state.submitting = true;
        state.error = null;
        state.success = false;
      })
      .addCase(bulkVerifyProviders.fulfilled, (state, action) => {
        state.submitting = false;
        state.success = true;
        state.message = action.payload?.message || 'Bulk verification completed';
      })
      .addCase(bulkVerifyProviders.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload || 'Failed to bulk verify providers';
        state.success = false;
      })

      // ============================================================
      // ADMIN: bulkRejectProviders
      // ============================================================
      .addCase(bulkRejectProviders.pending, (state) => {
        state.submitting = true;
        state.error = null;
        state.success = false;
      })
      .addCase(bulkRejectProviders.fulfilled, (state, action) => {
        state.submitting = false;
        state.success = true;
        state.message = action.payload?.message || 'Bulk rejection completed';
      })
      .addCase(bulkRejectProviders.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload || 'Failed to bulk reject providers';
        state.success = false;
      })
      
      // ============================================================
      // uploadVerificationDocument
      // ============================================================
      .addCase(uploadVerificationDocument.pending, (state) => {
        state.submitting = true;
        state.error = null;
        state.success = false;
      })
      .addCase(uploadVerificationDocument.fulfilled, (state, action) => {
        state.submitting = false;
        state.success = true;
        state.message = action.payload?.message || 'Document uploaded successfully';
        if (action.payload?.data) {
          const newDoc = action.payload.data;
          const exists = state.documents.some(doc => doc.id === newDoc.id);
          if (!exists) {
            state.documents = [...state.documents, newDoc];
          }
        }
      })
      .addCase(uploadVerificationDocument.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload || 'Failed to upload document';
        state.success = false;
      });
  }
});

// ============================================================

// SELECTORS
// ============================================================

export const selectVerificationStatus = (state) => state.businessVerification.verificationStatus;
export const selectVerified = (state) => state.businessVerification.verified;
export const selectVerificationData = (state) => state.businessVerification.verificationData;
export const selectDocuments = (state) => state.businessVerification.documents;
export const selectDocumentTypes = (state) => state.businessVerification.documentTypes;
export const selectRequirements = (state) => state.businessVerification.requirements;
export const selectHistory = (state) => state.businessVerification.history;
export const selectPendingVerifications = (state) => state.businessVerification.pendingVerifications;
export const selectStatistics = (state) => state.businessVerification.statistics;
export const selectStatuses = (state) => state.businessVerification.statuses;
export const selectVerificationLoading = (state) => state.businessVerification.loading;
export const selectVerificationSubmitting = (state) => state.businessVerification.submitting;
export const selectVerificationError = (state) => state.businessVerification.error;
export const selectVerificationSuccess = (state) => state.businessVerification.success;
export const selectVerificationMessage = (state) => state.businessVerification.message;

// ============================================================
// ACTIONS
// ============================================================

export const {
  resetVerificationState,
  clearVerificationError,
  clearVerificationSuccess,
  resetDocuments,
  resetPendingVerifications,
  resetStatistics 
} = businessVerificationSlice.actions;

// ============================================================
// EXPORT
// ============================================================

export default businessVerificationSlice.reducer;